import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Map } from 'immutable';
import { Observable, Subscription, of as observableOf } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import {
  State,
  selectActiveTimePeriods,
  selectCitationStyles,
  selectDefaultListAccess,
  selectImportances,
  selectLicenses,
} from '®app/ngrx';
import {
  CONTAINER_MODE,
  CONTAINER_ROOT_TYPE,
  ContainedTypeAndId,
  ContainerInitialisedAction,
  ContainerModes,
  ContainerStructure,
  ContainerType,
  LIST_ID,
  getContainedTypeAndIdOnly,
  getValidatedContainerMode,
  selectContainerStructure,
  selectListsById,
  selectReadingListDetails,
  selectSectionsById,
} from '®shared/container-structure';
import {
  CURRENT_USER,
  CurrentUserAndPermissions,
  CurrentUserFactoryProvider,
  CurrentUserService,
} from '®shared/current-user';
import { DashboardAnalyticsService } from '®shared/dashboard-analytics';
import { Loadable } from '®shared/data';
import { EventsService } from '®shared/events';
import {
  LAUNCH_METHOD,
  LaunchMethod,
  LaunchMethodFactoryProvider,
} from '®shared/events/launch-method.constant';
import {
  LAUNCH_MODE,
  LaunchMode,
  getValidatedLaunchMode,
} from '®shared/events/launch-mode.constant';
import { FocusService } from '®shared/focus';
import { GA_PAGE_NAMES } from '®shared/google-analytics';
import { GoogleAnalyticsService } from '®shared/google-analytics/google-analytics-ga4';
import {
  CitationStyleChangedAction,
  FilterItemsByQuickFilterAction,
  FilterItemsByResourceTypeFilterAction,
  FilterItemsByTextFilterAction,
  selectCitationsByItemForCurrentStyleHasErrors,
  selectCurrentCitationStyle,
  selectFilteredEntryIds,
  selectHasFilter,
  selectHasVisibleItems,
  selectItemCountByQuickFilters,
  selectItemCountByResourceType,
  selectOnlineResourcesByItemId,
  selectQuickFilter,
  selectResourceTypeFilter,
  selectTextFilter,
} from '®shared/list-item/store';
import {
  Permissions,
  UserPermissions,
  hasPermissionForListId,
} from '®shared/permissions';
import {
  BasicCitationStyle,
  CitationChangeEvent,
} from '®shared/reading-list/citation-switcher';
import {
  ArchiveListShowModalAction,
  DeleteListShowModalAction,
} from '®shared/reading-list/edit-modals';
import { createListEntryPointEvent } from '®shared/reading-list/events';
import { NodesEditorShowModalAction } from '®shared/reading-list/hierarchy/editor';
import { FilterItemsEvent } from '®shared/reading-list/items-filter';
import { ListDetailsEditorShowModalAction } from '®shared/reading-list/list-details/editor';
import { ListOwnersEditorShowModalAction } from '®shared/reading-list/list-owners';
import {
  EditableList,
  EditableSection,
  ReadingListDetails,
} from '®shared/reading-list/models';
import { OnlineResourcesByItemId } from '®shared/reading-list/online-resource-buttons';
import {
  ItemCountByQuickFilters,
  QuickFilter,
  QuickFilterEvent,
} from '®shared/reading-list/quick-filters';
import {
  ItemCountByResourceTypeFilter,
  ResourceTypeFilter,
  ResourceTypeFilterEvent,
} from '®shared/reading-list/resource-type-filter';
import { RequestReviewShowModalAction } from '®shared/reviews/request-review';
import { ReviewHistoryShowModalAction } from '®shared/reviews/review-history';
import {
  CitationStyle,
  Importance,
  InitialiseWithTenantAction,
  License,
  ListAccess,
  Period,
} from '®shared/tenant';
import { UriService } from '®shared/url-utils';
import {
  FetchListSubscriptionAction,
  FetchListSubscriptionSuccessAction,
  UpdateListSubscriptionAction,
} from '®shared/user/subscribed-lists/user-subscribed-lists.actions';
import { selectListSubscriptionStatus } from '®shared/user/subscribed-lists/user-subscribed-lists.state';
import {
  FetchUserAnnotationsForListAction,
  ReadingIntentionsByResourceId,
  StudyNotesByResourceId,
  areUserAnnotationsLoadedForList,
  selectReadingIntentionsByResourceForUser,
  selectStudyNotesByResourceForUser,
} from '®shared/user-annotations';
import { VirtualisationService } from '®shared/virtualisation';

import { ListRouteData, getListIdFromList } from '../shared';

export function getContainerModeFromList(
  route: ActivatedRoute,
): ContainerModes {
  const { data } = route.snapshot;
  return getValidatedContainerMode(data.containerMode);
}

export function getLaunchModeFromList(route: ActivatedRoute): LaunchMode {
  const { data } = route.snapshot;
  const { containerMode, isDraft = false } = data;
  return getValidatedLaunchMode(containerMode, isDraft);
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'rl-list-view',
  templateUrl: './list-view.component.html',
  providers: [
    {
      provide: CONTAINER_MODE,
      useFactory: getContainerModeFromList,
      deps: [ActivatedRoute],
    },
    { provide: CONTAINER_ROOT_TYPE, useValue: ContainerType.List },
    CurrentUserFactoryProvider,
    LaunchMethodFactoryProvider,
    {
      provide: LAUNCH_MODE,
      useFactory: getLaunchModeFromList,
      deps: [ActivatedRoute],
    },
    {
      provide: LIST_ID,
      useFactory: getListIdFromList,
      deps: [ActivatedRoute],
    },
  ],
})
export class ListViewComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild('stickyFilterBarElement', { static: true })
  stickyFilterBarElement: ElementRef;
  @ViewChild('stickyThingElement', { static: true })
  stickyThingSpacerElement: ElementRef;
  listDetails$: Observable<ReadingListDetails>;
  showBackButton: boolean;

  citationStyles$: Observable<CitationStyle[]>;
  containerRoot: ContainedTypeAndId;
  containerStructure$: Observable<ContainerStructure>;
  currentCitationStyle$: Observable<BasicCitationStyle>;
  defaultListAccess$: Observable<ListAccess | null>;
  editableList$: Observable<EditableList>;
  filteredEntryIds$: Observable<Map<string, boolean>>;
  itemCountByResourceTypeFilter$: Observable<ItemCountByResourceTypeFilter>;
  itemCountByQuickFilters$: Observable<ItemCountByQuickFilters>;
  hasFilter$: Observable<boolean>;
  hasItemCitationErrors$: Observable<boolean>;
  hasVisibleEntries$: Observable<boolean>;
  importances$: Observable<Importance[]>;
  licenses$: Observable<License[]>;
  quickFilter$: Observable<QuickFilter>;
  resourceTypeFilter$: Observable<ResourceTypeFilter>;
  sectionsById$: Observable<Map<string, EditableSection>>;
  textFilter$: Observable<string>;
  timePeriods$: Observable<Period[]>;

  private listId: string;
  private list: ReadingListDetails;
  permissions: UserPermissions;

  // User annotations, bound to the user/resource pair
  studyNotesByResource$: Observable<StudyNotesByResourceId>;
  readingIntentionsByResource$: Observable<ReadingIntentionsByResourceId>;
  areAnnotationsResolved$: Observable<boolean>;

  // Online Resource Buttons, keyed by list item ID
  onlineResourcesByItem$: Observable<OnlineResourcesByItemId>;

  // List Subscription Status
  listSubscription$: Observable<Loadable<boolean> | null>;

  stickyThingHeight: number;
  stickyThingWidth: number;
  private stickyFilterBarResizedSubscription: Subscription;
  private windowResizeSubscription: Subscription;
  private listSubscription: Subscription;

  showEmbedDifferentListButton = false;

  queryParamMap: ParamMap;

  constructor(
    @Inject(CONTAINER_MODE) public containerMode: ContainerModes,
    @Inject(CURRENT_USER) public currentUser: CurrentUserAndPermissions,
    private currentUserService: CurrentUserService,
    private dashboardAnalyticsService: DashboardAnalyticsService,
    private eventsService: EventsService,
    private focusService: FocusService,
    private googleAnalyticsService: GoogleAnalyticsService,
    @Inject(LAUNCH_METHOD) private launchMethod: LaunchMethod,
    @Inject(LAUNCH_MODE) private launchMode: LaunchMode,
    private route: ActivatedRoute,
    private store: Store<State>,
    private titleService: Title,
    private uriService: UriService,
    private virtualisationService: VirtualisationService,
    private router: Router,
  ) {
    this.citationStyles$ = this.store.select(selectCitationStyles);
    this.currentCitationStyle$ = this.store.select(selectCurrentCitationStyle);
    this.defaultListAccess$ = this.store.select(selectDefaultListAccess);
    this.hasItemCitationErrors$ = this.store.select(
      selectCitationsByItemForCurrentStyleHasErrors,
    );
    this.importances$ = this.store.select(selectImportances);
    this.licenses$ = this.store.select(selectLicenses);
    this.timePeriods$ = this.store.select(selectActiveTimePeriods);
    this.containerStructure$ = this.store.select(selectContainerStructure);
    this.sectionsById$ = this.store.select(selectSectionsById);

    this.textFilter$ = this.store.select(selectTextFilter);
    this.quickFilter$ = this.store.select(selectQuickFilter);
    this.resourceTypeFilter$ = this.store.select(selectResourceTypeFilter);
    this.hasFilter$ = this.store.select(selectHasFilter);
    this.filteredEntryIds$ = this.store.select(selectFilteredEntryIds);
    this.itemCountByResourceTypeFilter$ = this.store.select(
      selectItemCountByResourceType,
    );
    this.hasVisibleEntries$ = this.store.select(selectHasVisibleItems);

    this.filteredEntryIds$.forEach(() => {
      this.virtualisationService.forceAllVirtualisersToRecheckPosition();
    });

    this.queryParamMap = this.route.snapshot.queryParamMap;

    this.listDetails$ = this.store
      .select(selectReadingListDetails)
      .pipe(filter(container => (container ? true : false)));

    // @todo We want to transition, in time, to using
    // this EditableList interface for dealing with
    // changes to list details. And away from the `ReadingListDetails`
    // model we have been relying on. This currently handles
    // hasUnpublishedChanges and establishes the pattern we want to
    // move forward with.
    this.editableList$ = this.store
      .select(selectListsById)
      .pipe(map(listsById => listsById.get(this.listId)));

    this.listSubscription = this.listDetails$.subscribe(
      list => (this.list = list),
    );
  }

  ngOnInit() {
    this.route.data.forEach(this.extractDataFromActivatedRoute.bind(this));
    this.showBackButton =
      this.queryParamMap.get('navigateBackToLists') === 'true';

    this.showEmbedDifferentListButton =
      this.queryParamMap.has('lti1p3LaunchId') &&
      this.queryParamMap.has('existingResourceLinkId') &&
      this.list.is_archived &&
      hasPermissionForListId(
        Permissions.EDIT_LIST,
        this.listId,
        this.permissions,
      );
  }

  ngAfterViewInit() {
    if (this.route.snapshot.fragment) {
      this.focusService.scrollToElement(this.route.snapshot.fragment);
    }
  }

  ngOnDestroy(): void {
    if (this.windowResizeSubscription) {
      this.windowResizeSubscription.unsubscribe();
    }
    if (this.stickyFilterBarResizedSubscription) {
      this.stickyFilterBarResizedSubscription.unsubscribe();
    }
    if (this.listSubscription) {
      this.listSubscription.unsubscribe();
    }
  }

  /**
   * Extracts data from the ActivatedRoute and sets up the component
   *
   * @param {ListRouteData} data - resolved list view dependencies
   */
  private extractDataFromActivatedRoute(data: ListRouteData): void {
    // main data is retrieved from route resolvers
    const { readingListDetails } = data;

    const tenant = readingListDetails.tenant;
    this.store.dispatch(new InitialiseWithTenantAction(tenant));

    const listId = readingListDetails.id;
    this.listId = listId;

    this.containerRoot = getContainedTypeAndIdOnly(readingListDetails);
    const isDraft: boolean = readingListDetails.is_draft || false;

    this.store.dispatch(
      new ContainerInitialisedAction({ container: readingListDetails }),
    );

    this.onlineResourcesByItem$ = this.store.select(
      selectOnlineResourcesByItemId,
    );

    this.setWindowTitle(readingListDetails.title, tenant.name);

    const currentUrl = this.uriService.getTarlListViewUrlForAnalytics(
      this.listId,
      isDraft,
    );
    const isLoggedIn = !this.currentUser.oauthUser.isAnonymous;
    this.googleAnalyticsService.sendPageView(
      currentUrl,
      GA_PAGE_NAMES.LIST_OUTLINE_VIEW,
      isLoggedIn,
    );
    this.dashboardAnalyticsService.logListOutlineView(listId);

    this.eventsService.recordEvent(
      createListEntryPointEvent(
        this.listId,
        { type: 'lists', id: this.listId },
        this.launchMethod,
        this.launchMode,
      ),
    );

    this.initialiseUserContent(this.currentUser, listId);
  }

  initialiseUserContent(
    currentUser: CurrentUserAndPermissions,
    listId: string,
  ) {
    const { oauthUser, permissions } = currentUser;
    const userId = oauthUser.guid;

    this.permissions = permissions;

    // user-centric content
    // subscribe to list subscription state
    this.listSubscription$ = this.store.select(
      selectListSubscriptionStatus(this.listId),
    );
    if (!currentUser.hasTarlAccount) {
      // list subscriptions
      this.store.dispatch(
        new FetchListSubscriptionSuccessAction({
          listId: this.listId,
          isSubscribed: false,
        }),
      );
      // annotations subscriptions
      this.areAnnotationsResolved$ = observableOf(true);
      this.studyNotesByResource$ = observableOf({});
      this.readingIntentionsByResource$ = observableOf({});

      this.itemCountByQuickFilters$ = this.store.select(
        selectItemCountByQuickFilters(null),
      );
    } else {
      // trigger loading for list subscription state
      this.store.dispatch(
        new FetchListSubscriptionAction({
          userId,
          listId: this.listId,
        }),
      );

      // connect to observables for annotations for this list and user
      this.areAnnotationsResolved$ = this.store.select(
        areUserAnnotationsLoadedForList(userId, listId),
      );
      this.studyNotesByResource$ = this.store.select(
        selectStudyNotesByResourceForUser(userId),
      );
      this.readingIntentionsByResource$ = this.store.select(
        selectReadingIntentionsByResourceForUser(userId),
      );

      this.itemCountByQuickFilters$ = this.store.select(
        selectItemCountByQuickFilters(userId),
      );
      /**
       * Load the annotations for the requested list AFTER the list-subscription check.
       * This is because both APIs try to create a user in TARL
       * @see {@link https://github.com/techfromsage/shipshape-app/issues/1457}
       */
      this.listSubscription$
        .pipe(
          filter(
            listSubscription => listSubscription && !listSubscription.isLoading,
          ),
        )
        .forEach(() => {
          this.areAnnotationsResolved$
            .pipe(filter(areAnnotationsResolved => !areAnnotationsResolved))
            .forEach(() => {
              this.store.dispatch(
                new FetchUserAnnotationsForListAction({ listId, userId }),
              );
            });
        });
    }
  }

  /**
   * When the current list is added to user-subscribed lists, handle it.
   * @param {string} listId - the ID for the list to which we are subscribing
   */
  onAddToMyLists(listId) {
    const { oauthUser } = this.currentUser;
    if (oauthUser.isAnonymous) {
      return this.currentUserService.login();
    }
    this.store.dispatch(
      new UpdateListSubscriptionAction({
        userId: oauthUser.guid,
        listId,
      }),
    );
  }

  setWindowTitle(newTitle: string, tenantName: string) {
    this.titleService.setTitle(`${newTitle} | ${tenantName}`);
  }

  changeCitationStyle(event: CitationChangeEvent) {
    this.store.dispatch(
      new CitationStyleChangedAction({
        styleId: event.style.id,
      }),
    );
  }

  onTextFilter(event: FilterItemsEvent) {
    this.store.dispatch(
      new FilterItemsByTextFilterAction({
        filterText: event.filterTerm,
      }),
    );
  }

  onClearTextFilter() {
    this.onTextFilter({});
  }

  onQuickFilter(event: QuickFilterEvent) {
    this.store.dispatch(
      new FilterItemsByQuickFilterAction({
        quickFilter: event.quickFilter,
      }),
    );
  }

  onClearQuickFilter() {
    this.onQuickFilter({});
  }

  onResourceTypeFilter(event: ResourceTypeFilterEvent) {
    this.store.dispatch(
      new FilterItemsByResourceTypeFilterAction({
        resourceTypeFilter: event.resourceTypeFilter,
      }),
    );
  }

  onClearResourceTypeFilter() {
    this.onResourceTypeFilter({});
  }

  showArchiveListModal(action: ArchiveListShowModalAction) {
    this.store.dispatch(action);
  }

  showDeleteListModal(action: DeleteListShowModalAction) {
    this.store.dispatch(action);
  }

  showListDetailsEditorModal(action: ListDetailsEditorShowModalAction) {
    this.store.dispatch(action);
  }

  showAssignListOwnersModal(action: ListOwnersEditorShowModalAction) {
    this.store.dispatch(action);
  }

  showRequestReviewModal(action: RequestReviewShowModalAction) {
    this.store.dispatch(action);
  }

  showReviewHistoryModal(action: ReviewHistoryShowModalAction) {
    this.store.dispatch(action);
  }

  showStudentNumbersAndHierarchyModal(action: NodesEditorShowModalAction) {
    this.store.dispatch(action);
  }

  backToSuggestedLists() {
    const paramMap = this.route.snapshot.paramMap;
    const version = paramMap.get('version');
    const tenantCode = paramMap.get('tenantCode');

    // TODO Once released we have a follow up to remove the `launchId` backwards compatibility
    const launchId =
      this.route.snapshot.queryParamMap.get('lti1p3LaunchId') ||
      this.route.snapshot.queryParamMap.get('launchId');

    this.router.navigate(
      [version, tenantCode, 'resource_linking_request', launchId],
      {
        queryParamsHandling: 'preserve',
      },
    );
  }
  navigateToRelinkContentSelection() {
    const paramMap = this.route.snapshot.paramMap;
    const version = paramMap.get('version');
    const tenantCode = paramMap.get('tenantCode');

    // TODO Once released we have a follow up to remove the `launchId` backwards compatibility
    const launchId =
      this.route.snapshot.queryParamMap.get('lti1p3LaunchId') ||
      this.route.snapshot.queryParamMap.get('launchId');

    this.router.navigate(
      [version, tenantCode, 'resource_link_content_selection', launchId],
      {
        queryParamsHandling: 'preserve',
      },
    );
  }
}
